import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  User as FirebaseUser, 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '@/lib/firebase.ts';
import { AuthUser, AuthContextType } from '@/types/auth.ts';
import { clearAllPendingCreates } from '@/lib/invoice-create-operation';

// Auth State Types
interface AuthState {
  user: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
}

type AuthAction = 
  | { type: 'SIGN_IN_START' }
  | { type: 'SIGN_IN_SUCCESS'; payload: { user: AuthUser; firebaseUser: FirebaseUser } }
  | { type: 'SIGN_IN_ERROR'; payload: string }
  | { type: 'SIGN_OUT_SUCCESS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

// Create Auth Context
const AuthContext = createContext<AuthContextType | null>(null);

// Auth State Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SIGN_IN_START':
      return { ...state, loading: true, error: null };
    case 'SIGN_IN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        firebaseUser: action.payload.firebaseUser,
        loading: false,
        error: null
      };
    case 'SIGN_IN_ERROR':
      return {
        ...state,
        user: null,
        firebaseUser: null,
        loading: false,
        error: action.payload
      };
    case 'SIGN_OUT_SUCCESS':
      return {
        ...state,
        user: null,
        firebaseUser: null,
        loading: false,
        error: null
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial state
const initialState: AuthState = {
  user: null,
  firebaseUser: null,
  loading: true,
  error: null
};

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Stable deep-equality for two AuthUser profiles, used to skip a redundant
// SIGN_IN_SUCCESS dispatch on an unchanged-profile focus/refresh (which would
// otherwise re-render every useAuth consumer on EVERY window focus).
//
// `last_login` is deliberately EXCLUDED: it is regenerated to `now` on every
// fetchUserProfile call, so including it would make the profile "always
// changed" and defeat the guard. It is presentation-only and not a meaningful
// capability/profile change. Key order is normalized via a sorted-key replacer
// so two structurally-equal objects compare equal regardless of field order.
const stableStringify = (value: unknown): string => {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce((acc, k) => {
          acc[k] = (val as Record<string, unknown>)[k];
          return acc;
        }, {} as Record<string, unknown>);
    }
    return val;
  });
};

const authUsersEqual = (a: AuthUser | null, b: AuthUser | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  const strip = (u: AuthUser) => {
    const { last_login: _ignored, ...rest } = u as AuthUser & { last_login?: string };
    return rest;
  };
  return stableStringify(strip(a)) === stableStringify(strip(b));
};

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Latest user, read inside refreshUser WITHOUT making it a dependency — so the
  // focus listener (keyed on refreshUser) is not torn down + re-added on every
  // profile change, while the equality guard still compares against the current
  // user.
  const currentUserRef = useRef<AuthUser | null>(state.user);
  useEffect(() => {
    currentUserRef.current = state.user;
  }, [state.user]);


  // Helper function to get user profile from backend.
  // Throws on error so callers can surface the real message to the user
  // instead of swallowing it behind a generic "Failed to fetch user profile".
  const fetchUserProfile = useCallback(async (firebaseUser: FirebaseUser): Promise<AuthUser> => {
    // Get the ID token from Firebase
    const token = await firebaseUser.getIdToken();

    // Call the backend API to get user profile
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
    }
    const response = await fetch(`${apiUrl}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        await firebaseSignOut(auth);
        throw new Error('Your account is not authorized. If you were just invited, make sure you signed in with the same email address the invite was sent to.');
      }
      throw new Error(`Failed to fetch user profile (${response.status} ${response.statusText})`);
    }

    const data = await response.json();

    // Backend returns { user: {...}, timestamp: ... } so extract the user object
    const userData = data.user || data;

    // Transform backend response to AuthUser format
    const authUser: AuthUser = {
      user_id: userData.user_id || firebaseUser.uid,
      email: userData.email || firebaseUser.email!,
      name: userData.name || firebaseUser.displayName || firebaseUser.email!.split('@')[0],
      role: userData.role || 'user',
      permissions: userData.permissions || [],
      tenant_id: userData.tenant_id || 'default',
      tenant: userData.tenant || {
        tenant_id: userData.tenant_id || 'default',
        tenant_name: userData.tenant_name || 'BuildGravity',
        plan: 'pro',
        is_active: true,
        // R2: backend surfaces the tenant-level mcp_access flag on `tenant`.
        // When the backend provides `userData.tenant`, mcp_access is carried
        // through verbatim; this fallback (no tenant object) defaults to off.
        mcp_access: false,
      },
      is_active: userData.is_active !== false,
      last_login: new Date().toISOString(),
      modules: userData.modules || {},
      is_admin: userData.is_admin || false,
      is_super_admin: userData.is_super_admin || false,
      available_tenants: userData.available_tenants || []
    };

    return authUser;
  }, []);
  
  // Sign in with email and password
  const signInWithEmail = useCallback(async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'SIGN_IN_START' });
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const authUser = await fetchUserProfile(credential.user);

      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { user: authUser, firebaseUser: credential.user }
      });
    } catch (error: any) {
      dispatch({ type: 'SIGN_IN_ERROR', payload: error.message });
      throw error;
    }
  }, [fetchUserProfile]);

  // Sign in with Google
  const signIn = useCallback(async (): Promise<void> => {
    dispatch({ type: 'SIGN_IN_START' });
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const authUser = await fetchUserProfile(credential.user);

      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { user: authUser, firebaseUser: credential.user }
      });
    } catch (error: any) {
      dispatch({ type: 'SIGN_IN_ERROR', payload: error.message });
      throw error;
    }
  }, [fetchUserProfile]);

  // Create user with email and password
  const createUserWithEmail = useCallback(async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'SIGN_IN_START' });
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const authUser = await fetchUserProfile(credential.user);

      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { user: authUser, firebaseUser: credential.user }
      });
    } catch (error: any) {
      dispatch({ type: 'SIGN_IN_ERROR', payload: error.message });
      throw error;
    }
  }, [fetchUserProfile]);
  
  // Sign out — also clears offline queue/photos for security (AC #15)
  const signOut = useCallback(async (): Promise<void> => {
    try {
      // Clear offline IndexedDB data before signing out
      try {
        const { offlineQueue } = await import('@/lib/offline/offlineQueue');
        const { offlinePhotos } = await import('@/lib/offline/offlinePhotos');
        await Promise.all([offlineQueue.clearAll(), offlinePhotos.clearAll()]);
      } catch {
        // Non-fatal — IndexedDB may not be initialized yet
      }
      // Create Invoice's pending-intent records live in localStorage, which a logout does not clear. A stuck
      // one must never follow her (or the next person) into the next login.
      clearAllPendingCreates();
      await firebaseSignOut(auth);
      dispatch({ type: 'SIGN_OUT_SUCCESS' });
    } catch (error: any) {
      dispatch({ type: 'SIGN_IN_ERROR', payload: error.message });
      throw error;
    }
  }, []);
  
  // SPEC-connector-hardening R5 AC3: re-hydrate `user` from /auth/me without a
  // full reload. AuthProvider hydrates from /auth/me exactly ONCE (the onAuthState
  // listener) — that was the stale-gate root: an admin grant/revoke of
  // `ai_connector` (or a tenant `mcp_access` flip) did not surface until a hard
  // reload. This re-fetches the profile for the CURRENT firebase user and
  // dispatches it. Best-effort: a transient failure leaves the existing user in
  // place (never signs the user out on a refresh hiccup).
  const refreshUser = useCallback(async (): Promise<void> => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;
    try {
      const authUser = await fetchUserProfile(firebaseUser);
      // Equality guard: skip the dispatch when the freshly-fetched profile
      // deep-equals the current user. Without this, every window focus
      // re-dispatches SIGN_IN_SUCCESS with a structurally-identical user,
      // re-rendering every useAuth consumer for no reason.
      if (authUsersEqual(authUser, currentUserRef.current)) {
        return;
      }
      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { user: authUser, firebaseUser },
      });
    } catch (error) {
      // Non-fatal: keep the current user; a real auth failure is handled by the
      // onAuthStateChanged listener / fetchAPI's 401 path, not here.
      console.warn('refreshUser failed (keeping current user):', error);
    }
  }, [fetchUserProfile]);

  // Switch tenant (9.32)
  const switchTenant = useCallback(async (tenantId: string): Promise<void> => {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
    }
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      throw new Error('Not authenticated');
    }

    const token = await firebaseUser.getIdToken();

    const response = await fetch(`${apiUrl}/auth/switch-tenant/${tenantId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to switch tenant' }));
      throw new Error(error.detail || 'Failed to switch tenant');
    }

    // Reload to apply the new tenant (backend persists the choice to Firestore)
    window.location.reload();
  }, []);
  
  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Check if user has a specific permission
  const can = useCallback((permission: string): boolean => {
    if (!state.user) return false;
    // Admin users have all permissions
    if (state.user.is_admin) return true;
    // Check specific permission in modules
    const moduleValue = state.user.modules?.[permission];
    // Permission is granted if value is true, or an array (meaning user has access to specific items)
    // Permission is denied if value is false, undefined, or 'read_only' (read_only means no write access)
    return moduleValue === true || Array.isArray(moduleValue);
  }, [state.user]);

  // Check if user has read-only access to a permission
  const isReadOnly = useCallback((permission: string): boolean => {
    if (!state.user) return false;
    // Admin users are never read-only
    if (state.user.is_admin) return false;
    // Check if module is marked as read_only
    return state.user.modules?.[permission] === 'read_only';
  }, [state.user]);
  
  // Listen to auth state changes
  useEffect(() => {
    // Track if component is still mounted
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && isMounted) {
        try {
          // Don't pass abort signal - let auth requests complete naturally
          const authUser = await fetchUserProfile(firebaseUser);
          if (authUser && isMounted) {
            dispatch({
              type: 'SIGN_IN_SUCCESS',
              payload: { user: authUser, firebaseUser }
            });
          } else if (isMounted) {
            dispatch({ type: 'SIGN_OUT_SUCCESS' });
          }
        } catch (error) {
          if (isMounted) {
            console.error('Error in auth state change:', error);
            dispatch({ type: 'SIGN_OUT_SUCCESS' });
          }
        }
      } else if (!firebaseUser && isMounted) {
        dispatch({ type: 'SIGN_OUT_SUCCESS' });
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [fetchUserProfile]);

  // SPEC-connector-hardening R5 AC3: re-hydrate /auth/me on window focus so a
  // grant/revoke made elsewhere (an admin enabling ai_connector, a tenant
  // mcp_access flip) is reflected when the user returns to the tab — no full
  // reload. Only fires while authenticated; refreshUser is itself a no-op when
  // there is no current firebase user.
  useEffect(() => {
    if (!state.user) return;
    const onFocus = () => { void refreshUser(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [state.user, refreshUser]);

  // Memoized context value
  const contextValue = useMemo((): AuthContextType => ({
    user: state.user,
    isLoading: state.loading,
    isAuthenticated: !!state.user,
    error: state.error,
    signIn,
    signInWithEmail,
    createUserWithEmail,
    signOut,
    switchTenant,
    clearError,
    can,
    isReadOnly,
    refreshUser
  }), [
    state,
    signIn,
    signInWithEmail,
    createUserWithEmail,
    signOut,
    switchTenant,
    clearError,
    can,
    isReadOnly,
    refreshUser
  ]);
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};